import React, { useEffect, useMemo, useState } from "react";

import HttpManager from "../../../services/HttpManager";
import { EquipoConfig, MetasTablero, SemanasTablero } from "../../../services/v2/prospects.service";
import { Multi } from "../managment/enrollmentRequests/filtrosMatriculas";
import "../managment/enrollmentRequests/filtrosMatriculas.css";
import { errorTexto } from "./salesHelpers";
import { logoDe } from "./tableroView";
import { deIso, iso, rangoTexto } from "./tableroEquipos";
import { tocar } from "../../../festejo/sonidos";

interface Props { campusId: number; mes: string; onGuardado: () => void; }

const PAIS: { [k: string]: string } = { hn: "Honduras", mx: "México" };
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const mesTexto = (m: string) => { const [y, mm] = m.split("-").map(Number); return MESES[mm - 1] + " " + y; };
const sumaDias = (d: string, n: number) => { const x = deIso(d); return iso(new Date(x.getFullYear(), x.getMonth(), x.getDate() + n)); };
const largo = (desde: string, hasta: string) => Math.round((deIso(hasta).getTime() - deIso(desde).getTime()) / 86400000) + 1;
const num = (v: number) => Number(v).toLocaleString("es-MX", { maximumFractionDigits: 1 });
const SONIDO_NOMBRE: { [k: string]: string } = { lobo: "Aullido (Alpha)", diamante: "Campanillas (Diamond)", ateneas: "Tambores (Ateneas)", fuego: "Fénix", jaguar: "Rugido (Jaguar)", aguila: "Águila", fanfarria: "Fanfarria", trompeta: "Trompeta", orquesta: "Orquesta" };

/**
 * Semanas de meta del mes (Esteban, 4-nov-2026): cada mes sale solo con la regla (4 semanas de 7 días; los días que sobran van
 * a la 1, la 3 y la 4, en ese orden) y Dirección o Super Admin lo ajustan aquí. Se edita
 * el día en que cierra cada semana; la siguiente empieza al día siguiente. La primera empieza el 1 y la última termina el último día.
 */
const SemanasMeta: React.FC<{ campusId: number; mes: string; onGuardado: () => void }> = ({ campusId, mes, onGuardado }) => {
	const http = HttpManager.getInstance();
	const [datos, setDatos] = useState<SemanasTablero | null>(null);
	const [cierres, setCierres] = useState<string[]>([]);   // "hasta" de cada semana (la última siempre es el fin de mes)
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const primero = mes + "-01";
	const ultimo = iso(new Date(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)), 0));

	const poner = (d: SemanasTablero) => { setDatos(d); setCierres(d.semanas.map((s) => s.hasta)); };
	useEffect(() => {
		setDatos(null); setError(null); setAviso(null);
		http.prospectsService.tableroSemanas(campusId, mes).then((r) => poner(r.data)).catch((e) => setError(errorTexto(e)));
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [campusId, mes]);

	const semanas = cierres.map((h, i) => ({ n: i + 1, desde: i === 0 ? primero : sumaDias(cierres[i - 1], 1), hasta: h }));
	const cambiado = !!datos && cierres.join() !== datos.semanas.map((s) => s.hasta).join();
	const invalido = semanas.some((s) => s.desde > s.hasta || s.hasta > ultimo) || cierres[cierres.length - 1] !== ultimo;
	const guardar = (lista: { desde: string; hasta: string }[]) => {
		setBusy(true); setError(null); setAviso(null);
		http.prospectsService.guardarSemanas(campusId, mes, lista)
			.then((r) => { poner(r.data); setAviso(lista.length ? "Semanas guardadas." : "Volvió a la regla automática."); onGuardado(); })
			.catch((e) => setError(errorTexto(e)))
			.finally(() => setBusy(false));
	};
	const partir = () => {
		// La última semana se parte a la mitad.
		const u = semanas[semanas.length - 1];
		if (!u || largo(u.desde, u.hasta) < 2) return;
		const medio = sumaDias(u.desde, Math.floor(largo(u.desde, u.hasta) / 2) - 1);
		setCierres([...cierres.slice(0, -1), medio, ultimo]);
	};
	const quitar = (i: number) => setCierres(cierres.filter((_, j) => j !== i));

	if (error && !datos) return <div className="aviso error">{error}</div>;
	if (!datos) return <div className="ventas-vacio">Cargando…</div>;
	return (
		<div className="tarjeta tb-ventas">
			<div className="tarjeta-cab">
				<h4>Semanas de meta</h4>
				<span className="apagado">{mesTexto(mes)} · {datos.automatico ? "regla automática" : "ajustadas por Dirección"}{datos.puedeEditar ? "" : " · solo Dirección las cambia"}</span>
			</div>
			<div className="waScrollX">
				<table className="tb-tabla tb-semanas">
					<thead><tr><th>Semana</th><th>Desde</th><th>Cierra</th><th>Días</th>{datos.puedeEditar ? <th /> : null}</tr></thead>
					<tbody>
						{semanas.map((s, i) => {
							const ultima = i === semanas.length - 1;
							return (
								<tr key={i} className={datos.semanas[i]?.hasta !== s.hasta ? "tb-meta-cambiada" : ""}>
									<td><b>Semana {s.n}</b><span className="apagado" style={{ marginLeft: 8, fontSize: 12 }}>{s.desde <= s.hasta ? rangoTexto(s.desde, s.hasta) : "—"}</span></td>
									<td className="apagado">{Number(s.desde.slice(8))}</td>
									<td>
										{datos.puedeEditar && !ultima
											? <input type="date" value={s.hasta} min={s.desde} max={sumaDias(ultimo, -1)} disabled={busy} aria-label={"Cierre de la semana " + s.n}
												onChange={(ev) => { if (ev.target.value) setCierres(cierres.map((c, j) => (j === i ? ev.target.value : c))); }} />
											: <b>{Number(s.hasta.slice(8))}</b>}
									</td>
									<td className="apagado">{s.desde <= s.hasta ? largo(s.desde, s.hasta) : "—"}</td>
									{datos.puedeEditar ? <td>{i > 0 ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => quitar(i)}>Unir con la anterior</button> : null}</td> : null}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
			{aviso ? <div className="aviso">{aviso}</div> : null}
			{datos.puedeEditar ? (
				<div className="tb-editar-botones">
					<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !cambiado || invalido} onClick={() => guardar(semanas.map((s) => ({ desde: s.desde, hasta: s.hasta })))}>Guardar semanas</button>
					<button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy || semanas.length >= 8} onClick={partir}>Partir la última</button>
					{cambiado ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => setCierres(datos.semanas.map((s) => s.hasta))}>Deshacer</button> : null}
					{!datos.automatico ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => guardar([])}>Volver a la regla automática</button> : null}
				</div>
			) : null}
		</div>
	);
};

type Borrador = { meta: string; metaSemanal: string; semanas: { [desde: string]: string } };
const kDe = (e: { pais: string; equipo: string }) => e.pais + "|" + e.equipo;

/**
 * Metas por equipo (Esteban, 4-nov-2026): la meta del mes, la proyección semanal base (40 al nacer) y la proyección de cada
 * semana de meta del mes (en blanco = la base). Super Admin pone todas; cada directora las de sus equipos (Suamy HN + Online,
 * Lizzeth MTY, Christy TRC, Claudia Admisiones). Los demás solo las ven.
 */
const MetasEquipos: React.FC<{ campusId: number; mes: string; datos: MetasTablero; onDatos: (d: MetasTablero) => void; onGuardado: () => void }> = ({ campusId, mes, datos, onDatos, onGuardado }) => {
	const http = HttpManager.getInstance();
	const borradorDe = (d: MetasTablero): { [k: string]: Borrador } => {
		const b: { [k: string]: Borrador } = {};
		d.equipos.forEach((e) => {
			const sem: { [desde: string]: string } = {};
			d.metasSemana.filter((m) => m.pais === e.pais && m.equipo === e.equipo).forEach((m) => { sem[m.desde] = m.ajustada ? String(m.meta) : ""; });
			b[kDe(e)] = { meta: String(e.meta), metaSemanal: String(e.metaSemanal), semanas: sem };
		});
		return b;
	};
	const [b, setB] = useState<{ [k: string]: Borrador }>(() => borradorDe(datos));
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	useEffect(() => { setB(borradorDe(datos)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [datos]);

	const equipos = datos.equipos.filter((e) => e.activo);
	const semanasDe = (e: EquipoConfig) => datos.metasSemana.filter((m) => m.pais === e.pais && m.equipo === e.equipo);
	const valido = (v: string) => v === "" || (Number(v) >= 0 && Number(v) <= 9999);
	const cambios = useMemo(() => {
		const metas: { pais: string; equipo: string; meta?: number; metaSemanal?: number }[] = [];
		const semanas: { pais: string; equipo: string; desde: string; meta: number | null }[] = [];
		let invalido = false;
		equipos.forEach((e) => {
			const x = b[kDe(e)]; if (!x) return;
			const m: { pais: string; equipo: string; meta?: number; metaSemanal?: number } = { pais: e.pais, equipo: e.equipo };
			if (x.meta !== "" && Number(x.meta) !== e.meta) m.meta = Number(x.meta);
			if (x.metaSemanal !== "" && Number(x.metaSemanal) !== e.metaSemanal) m.metaSemanal = Number(x.metaSemanal);
			if (x.meta === "" || x.metaSemanal === "" || !valido(x.meta) || !valido(x.metaSemanal)) invalido = true;
			if (m.meta !== undefined || m.metaSemanal !== undefined) metas.push(m);
			semanasDe(e).forEach((s) => {
				const v = x.semanas[s.desde] ?? "";
				if (!valido(v)) invalido = true;
				const antes = s.ajustada ? String(s.meta) : "";
				if (v !== antes) semanas.push({ pais: e.pais, equipo: e.equipo, desde: s.desde, meta: v === "" ? null : Number(v) });
			});
		});
		return { metas, semanas, invalido };
		/* eslint-disable-next-line react-hooks/exhaustive-deps */
	}, [b, datos]);
	const hayCambios = cambios.metas.length + cambios.semanas.length > 0;

	const guardar = async () => {
		setBusy(true); setError(null); setAviso(null);
		try {
			let r: MetasTablero = datos;
			if (cambios.metas.length) r = (await http.prospectsService.guardarMetas(campusId, cambios.metas, mes)).data;
			if (cambios.semanas.length) r = (await http.prospectsService.guardarMetasSemana(campusId, cambios.semanas, mes)).data;
			onDatos(r);
			setAviso("Metas guardadas.");
			onGuardado();
		} catch (e) { setError(errorTexto(e)); }
		setBusy(false);
	};
	const poner = (e: EquipoConfig, campo: "meta" | "metaSemanal", v: string) => setB({ ...b, [kDe(e)]: { ...b[kDe(e)], [campo]: v } });
	const ponerSemana = (e: EquipoConfig, desde: string, v: string) => setB({ ...b, [kDe(e)]: { ...b[kDe(e)], semanas: { ...b[kDe(e)].semanas, [desde]: v } } });
	const semanas = datos.semanas;
	const editaAlguno = equipos.some((e) => e.puedeEditar);
	const tocado = (e: EquipoConfig) => cambios.metas.some((m) => m.pais === e.pais && m.equipo === e.equipo) || cambios.semanas.some((m) => m.pais === e.pais && m.equipo === e.equipo);

	return (
		<div className="tarjeta tb-ventas">
			<div className="tarjeta-cab">
				<h4>Metas por equipo</h4>
				<span className="apagado">{mesTexto(mes)} · matrículas · {datos.superAdmin ? "Super Admin pone todas" : editaAlguno ? "pones las de tus equipos" : "solo Dirección las cambia"}</span>
			</div>
			<div className="waScrollX">
				<table className="tb-tabla tb-metas">
					<thead>
						<tr>
							<th>Equipo</th><th>Meta del mes</th><th>Proyección semanal</th>
							{semanas.map((s) => <th key={s.n} className="tb-sem">Semana {s.n}<small>{rangoTexto(s.desde, s.hasta)}</small></th>)}
						</tr>
					</thead>
					<tbody>
						{equipos.map((e) => {
							const x = b[kDe(e)] || { meta: "", metaSemanal: "", semanas: {} };
							const ms = semanasDe(e);
							const puede = e.puedeEditar;
							return (
								<tr key={kDe(e)} className={tocado(e) ? "tb-meta-cambiada" : ""}>
									<td><span className="tb-meta-eq" style={{ "--col": e.color || "var(--wa-apagado)" } as React.CSSProperties}><img src={logoDe(e)} alt="" />{e.equipo}<small className="apagado"> {PAIS[e.pais]}</small></span></td>
									<td>{puede ? <input type="number" min={0} max={9999} step={1} value={x.meta} disabled={busy} aria-label={"Meta del mes de " + e.equipo} onChange={(ev) => poner(e, "meta", ev.target.value)} style={{ width: 84 }} /> : <b>{num(e.meta)}</b>}</td>
									<td>{puede ? <input type="number" min={0} max={9999} step={1} value={x.metaSemanal} disabled={busy} aria-label={"Proyección semanal de " + e.equipo} onChange={(ev) => poner(e, "metaSemanal", ev.target.value)} style={{ width: 84 }} /> : <b>{num(e.metaSemanal)}</b>}</td>
									{semanas.map((s) => {
										const m = ms.find((y) => y.desde === s.desde);
										const v = x.semanas[s.desde] ?? "";
										return (
											<td key={s.n} className="tb-sem">
												{puede && datos.semanalConfigurable
													? <input type="number" min={0} max={9999} step={1} value={v} placeholder={num(Number(x.metaSemanal) || e.metaSemanal)} disabled={busy} aria-label={"Proyección de " + e.equipo + " semana " + s.n} onChange={(ev) => ponerSemana(e, s.desde, ev.target.value)} style={{ width: 72 }} className={v === "" ? "tb-base" : ""} />
													: <span className={m?.ajustada ? "" : "apagado"}>{m ? num(m.meta) : num(e.metaSemanal)}</span>}
											</td>
										);
									})}
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			{!datos.semanalConfigurable && editaAlguno ? <div className="apagado" style={{ fontSize: 12 }}>La proyección por semana se podrá ajustar cuando se active la tabla en el servidor; mientras, vale la base de cada equipo.</div> : null}
			{error ? <div className="aviso error">{error}</div> : null}
			{aviso ? <div className="aviso">{aviso}</div> : null}
			{editaAlguno ? (
				<div className="tb-editar-botones">
					<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !hayCambios || cambios.invalido} onClick={guardar}>
						{cambios.metas.length + cambios.semanas.length > 1 ? "Guardar " + (cambios.metas.length + cambios.semanas.length) + " cambios" : "Guardar"}
					</button>
					{hayCambios ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => setB(borradorDe(datos))}>Deshacer</button> : null}
				</div>
			) : null}
		</div>
	);
};

type EquipoBorrador = Partial<EquipoConfig> & { _k: string; _nuevo?: boolean };

/**
 * Equipos configurables (Esteban, 4-nov-2026: "que superadmin pueda hacerlo para no tener que programar esto cada vez que hayan
 * cambios"): nombre, país, orden, activo, color, sonido, canal de WappTeam (su ícono es el logo), roles de rueda del equipo (además
 * de la regla por nombre; el rol de rueda sale de Personal), supervisoras y directoras. Solo Super Admin.
 */
const EquiposConfig: React.FC<{ campusId: number; mes: string; datos: MetasTablero; onDatos: (d: MetasTablero) => void; onGuardado: () => void }> = ({ campusId, mes, datos, onDatos, onGuardado }) => {
	const http = HttpManager.getInstance();
	const desde = (d: MetasTablero): EquipoBorrador[] => d.equipos.map((e) => ({ ...e, _k: String(e.id) }));
	const [lista, setLista] = useState<EquipoBorrador[]>(() => desde(datos));
	const [abierto, setAbierto] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [aviso, setAviso] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	useEffect(() => { setLista(desde(datos)); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [datos]);

	const gente = useMemo(() => datos.gente.map((g) => ({ id: g.clave, name: g.nombre, detalle: g.rol })), [datos.gente]);
	const rolesRueda = useMemo(() => Array.from(new Set(datos.gente.map((g) => g.rol).filter(Boolean))).sort().map((r) => ({ id: r.toLowerCase(), name: r })), [datos.gente]);
	const original = useMemo(() => { const m: { [k: string]: EquipoConfig } = {}; datos.equipos.forEach((e) => { m[String(e.id)] = e; }); return m; }, [datos.equipos]);
	const igual = (a: EquipoBorrador) => {
		const o = original[a._k]; if (!o) return false;
		return (["equipo", "pais", "orden", "activo", "color", "sonido", "canalId"] as const).every((c) => (a[c] ?? null) === (o[c] ?? null))
			&& JSON.stringify(a.roles || []) === JSON.stringify(o.roles) && JSON.stringify(a.supervisoras || []) === JSON.stringify(o.supervisoras) && JSON.stringify(a.directoras || []) === JSON.stringify(o.directoras);
	};
	const cambiados = lista.filter((e) => !igual(e));
	const invalido = lista.some((e) => !(e.equipo || "").trim() || (e.color && !/^#[0-9a-fA-F]{6}$/.test(e.color)));
	const pon = (k: string, cambio: Partial<EquipoConfig>) => setLista(lista.map((e) => (e._k === k ? { ...e, ...cambio } : e)));
	const agregar = () => {
		const k = "n" + Date.now();
		setLista([...lista, { _k: k, _nuevo: true, equipo: "", pais: "hn", orden: lista.length + 1, activo: true, color: "#2B62E0", sonido: "fanfarria", canalId: null, roles: [], supervisoras: [], directoras: [], meta: 100, metaSemanal: 40, logo: null }]);
		setAbierto(k);
	};
	const guardar = () => {
		setBusy(true); setError(null); setAviso(null);
		http.prospectsService.guardarEquipos(campusId, cambiados.map(({ _k, _nuevo, logo, puedeEditar, ...e }) => e), mes)
			.then((r) => { onDatos(r.data); setAviso(cambiados.length === 1 ? "Equipo guardado." : cambiados.length + " equipos guardados."); setAbierto(null); onGuardado(); })
			.catch((e) => setError(errorTexto(e)))
			.finally(() => setBusy(false));
	};
	const nombreDe = (clave: string) => datos.gente.find((g) => g.clave === clave)?.nombre || clave;

	return (
		<div className="tarjeta tb-ventas">
			<div className="tarjeta-cab">
				<h4>Equipos</h4>
				<span className="apagado">Quién es de cada equipo, quién pone sus metas y cómo se ve y suena · solo Super Admin</span>
			</div>
			{!datos.configurable ? <div className="aviso">Los equipos se podrán configurar cuando se active la tabla en el servidor.</div> : null}
			<div className="waScrollX">
				<table className="tb-tabla tb-equipos">
					<thead><tr><th>Equipo</th><th>País</th><th>Orden</th><th>Activo</th><th>Directoras</th><th>Supervisoras</th><th /></tr></thead>
					<tbody>
						{lista.map((e) => {
							const abre = abierto === e._k;
							return (
								<React.Fragment key={e._k}>
									<tr className={!igual(e) ? "tb-meta-cambiada" : ""}>
										<td><span className="tb-meta-eq" style={{ "--col": e.color || "var(--wa-apagado)" } as React.CSSProperties}><img src={logoDe({ equipo: e.equipo || "", logo: e.logo || null, color: e.color || null })} alt="" />{e.equipo || <i className="apagado">Nuevo equipo</i>}</span></td>
										<td className="apagado">{PAIS[e.pais || "hn"]}</td>
										<td className="apagado">{e.orden}</td>
										<td>{e.activo ? "Sí" : <span className="apagado">No</span>}</td>
										<td className="apagado">{(e.directoras || []).map(nombreDe).join(", ") || "—"}</td>
										<td className="apagado">{(e.supervisoras || []).map(nombreDe).join(", ") || "—"}</td>
										<td><button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={!datos.configurable && !e._nuevo} onClick={() => setAbierto(abre ? null : e._k)}>{abre ? "Cerrar" : "Editar"}</button></td>
									</tr>
									{abre ? (
										<tr className="tb-equipo-editor"><td colSpan={7}>
											<div className="tb-equipo-campos">
												<label>Nombre<input type="text" value={e.equipo || ""} maxLength={60} disabled={busy} onChange={(ev) => pon(e._k, { equipo: ev.target.value })} /></label>
												<label>País<select value={e.pais || "hn"} disabled={busy} onChange={(ev) => pon(e._k, { pais: ev.target.value as "hn" | "mx" })}><option value="hn">Honduras</option><option value="mx">México</option></select></label>
												<label>Orden<input type="number" min={0} max={99} value={e.orden ?? 0} disabled={busy} onChange={(ev) => pon(e._k, { orden: Number(ev.target.value) })} style={{ width: 70 }} /></label>
												<label className="tb-check"><input type="checkbox" checked={!!e.activo} disabled={busy} onChange={(ev) => pon(e._k, { activo: ev.target.checked })} /> Activo (compite en el podio)</label>
												<label>Color<span className="tb-color"><input type="color" value={e.color || "#888888"} disabled={busy} onChange={(ev) => pon(e._k, { color: ev.target.value })} /><input type="text" value={e.color || ""} placeholder="#RRGGBB" disabled={busy} onChange={(ev) => pon(e._k, { color: ev.target.value })} style={{ width: 92 }} /></span></label>
												<label>Sonido<span className="tb-color"><select value={e.sonido || ""} disabled={busy} onChange={(ev) => pon(e._k, { sonido: ev.target.value })}><option value="">Sin sonido</option>{datos.sonidos.map((s) => <option key={s} value={s}>{SONIDO_NOMBRE[s] || s}</option>)}</select>{e.sonido ? <button type="button" className="btn btn-link btn-sm" onClick={() => tocar(e.sonido || "")}>Oír</button> : null}</span></label>
												<label>Canal de WappTeam (su ícono es el logo)<input type="number" min={1} value={e.canalId ?? ""} placeholder="id del canal" disabled={busy} onChange={(ev) => pon(e._k, { canalId: ev.target.value === "" ? null : Number(ev.target.value) })} style={{ width: 110 }} /></label>
											</div>
											<div className="tb-equipo-campos">
												<div className="tb-pick"><span>Directoras (ponen las metas)</span><Multi titulo="Directoras" buscar opciones={gente} elegidos={e.directoras || []} onChange={(v) => pon(e._k, { directoras: v })} /></div>
												<div className="tb-pick"><span>Supervisoras</span><Multi titulo="Supervisoras" buscar opciones={gente} elegidos={e.supervisoras || []} onChange={(v) => pon(e._k, { supervisoras: v })} /></div>
												<div className="tb-pick"><span>Roles de rueda del equipo (además de «Asesora {e.equipo || "…"}»)</span><Multi titulo="Roles" buscar opciones={rolesRueda} elegidos={e.roles || []} onChange={(v) => pon(e._k, { roles: v })} /></div>
											</div>
											<div className="apagado" style={{ fontSize: 12 }}>Las asesoras entran al equipo por su rol de rueda en Personal: «Asesora {e.equipo || "…"}», «Supervisión {e.equipo || "…"}» o los roles que elijas aquí. Cambiar el nombre no cambia las ventas ya contadas.</div>
										</td></tr>
									) : null}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
			{error ? <div className="aviso error">{error}</div> : null}
			{aviso ? <div className="aviso">{aviso}</div> : null}
			<div className="tb-editar-botones">
				<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={busy || !cambiados.length || invalido || !datos.configurable} onClick={guardar}>{cambiados.length > 1 ? "Guardar " + cambiados.length + " equipos" : "Guardar equipo"}</button>
				<button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy || !datos.configurable} onClick={agregar}>Nuevo equipo</button>
				{cambiados.length ? <button type="button" className="btn btn-link btn-sm" disabled={busy} onClick={() => { setLista(desde(datos)); setAbierto(null); }}>Deshacer</button> : null}
			</div>
		</div>
	);
};

/** Pestaña Metas: metas por equipo, semanas de meta y (Super Admin) los equipos. */
const TableroMetas: React.FC<Props> = ({ campusId, mes, onGuardado }) => {
	const http = HttpManager.getInstance();
	const [datos, setDatos] = useState<MetasTablero | null>(null);
	const [error, setError] = useState<string | null>(null);
	const cargar = () => http.prospectsService.tableroEquipos(campusId, mes).then((r) => setDatos(r.data)).catch((e) => setError(errorTexto(e)));
	useEffect(() => { setDatos(null); setError(null); cargar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [campusId, mes]);

	if (error && !datos) return <div className="aviso error">{error}</div>;
	if (!datos) return <div className="ventas-vacio">Cargando…</div>;
	return (
		<div className="tb-metas-dos">
			<MetasEquipos campusId={campusId} mes={mes} datos={datos} onDatos={setDatos} onGuardado={onGuardado} />
			<SemanasMeta campusId={campusId} mes={mes} onGuardado={() => { onGuardado(); cargar(); }} />
			{datos.superAdmin ? <EquiposConfig campusId={campusId} mes={mes} datos={datos} onDatos={setDatos} onGuardado={onGuardado} /> : null}
		</div>
	);
};

export default TableroMetas;
